import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchUsageMetrics } from "../../client/UsageAnalyticsClient";
import { useAuth } from "../../../context/AuthContext";
import "./EvalLandingPage.css";
import { EVAL_AGENTS } from "../../agents";

// Descriptions are translated by id (landing.agents.<id>).
const AGENTS = EVAL_AGENTS;

interface AgentStats {
  activeUsers: number | null;
  sessions: number | null;
  hasPermission: boolean;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return n.toLocaleString("en-US");
  return String(n);
}

function IconBell() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function IconHelp() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1.08 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z" />
    </svg>
  );
}

function IconFeedback() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

export default function EvalLandingPage() {
  const { t } = useTranslation("eval");
  const { uid } = useAuth();
  const [stats, setStats] = useState<Record<string, AgentStats>>({});

  useEffect(() => {
    AGENTS.forEach((agent) => {
      fetchUsageMetrics(agent.id, "30d")
        .then((data) => {
          setStats((prev) => ({
            ...prev,
            [agent.id]: {
              activeUsers: data.activeUsers,
              sessions: data.sessions,
              hasPermission: true,
            },
          }));
        })
        .catch(() => {
          setStats((prev) => ({
            ...prev,
            [agent.id]: { activeUsers: null, sessions: null, hasPermission: false },
          }));
        });
    });
  }, []);

  return (
    <div className="eval-landing">
      {/* Header bar */}
      <header className="eval-landing__header">
        <div className="eval-landing__header-left">
          {/* Way back to the app the Rocky sidebar sent the user from. */}
          <Link className="eval-landing__home-link" to="/">
            <span aria-hidden="true">← </span>{t("backToRocky")}
          </Link>
        </div>
        {/* `--inert`: placeholder icons with no handler yet — hidden on phones. */}
        <div className="eval-landing__header-right">
          <button className="eval-landing__header-icon eval-landing__header-icon--inert"><IconFeedback /></button>
          <span className="eval-landing__header-sep" />
          <button className="eval-landing__header-icon eval-landing__header-icon--inert"><IconBell /></button>
          <span className="eval-landing__header-sep" />
          <button className="eval-landing__header-icon eval-landing__header-icon--inert"><IconHelp /></button>
          <span className="eval-landing__header-sep" />
          <button className="eval-landing__header-icon"><IconSettings /></button>
          <span className="eval-landing__header-sep" />
          <span className="eval-landing__header-user">{uid} ▾</span>
        </div>
      </header>

      {/* Main content */}
      <div className="eval-landing__content">
        <div className="eval-landing__hero">
          <h1 className="eval-landing__title">{t("landing.title")}</h1>
          <p className="eval-landing__description">
            {t("landing.description")}
          </p>
        </div>

        <div className="eval-landing__cards-wrapper">
          <div className="eval-landing__cards">
            {AGENTS.map((agent) => {
              const s = stats[agent.id];
              const hasPermission = s?.hasPermission !== false;
              return (
                <Link
                  key={agent.id}
                  to={`/eval?agent=${agent.id}`}
                  className={"eval-landing__card" + (!hasPermission ? " eval-landing__card--disabled" : "")}
                >
                  <h2 className="eval-landing__card-name">{agent.label}</h2>
                  <p className="eval-landing__card-desc-label">{t("landing.descLabel")}</p>
                  <p className="eval-landing__card-desc">{t(`landing.agents.${agent.id}`)}</p>
                  <div className="eval-landing__card-stats">
                    {hasPermission ? (
                      <>
                        <div className="eval-landing__card-stat">
                          <span className="eval-landing__card-stat-value">
                            {s?.activeUsers != null ? formatNumber(s.activeUsers) : "–"}
                          </span>
                          <span className="eval-landing__card-stat-label">{t("landing.activeUsers")}</span>
                        </div>
                        <div className="eval-landing__card-stat">
                          <span className="eval-landing__card-stat-value">
                            {s?.sessions != null ? formatNumber(s.sessions) : "–"}
                          </span>
                          <span className="eval-landing__card-stat-label">{t("landing.sessions")}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="eval-landing__card-stat">
                          <span className="eval-landing__card-stat-value">{"–"}</span>
                          <span className="eval-landing__card-stat-label">{t("landing.noPermission")}</span>
                        </div>
                        <div className="eval-landing__card-stat">
                          <span className="eval-landing__card-stat-value">{"–"}</span>
                          <span className="eval-landing__card-stat-label">{t("landing.noPermission")}</span>
                        </div>
                      </>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
